import { isPointInRing } from '../geo/point-in-ring';
import {
  DriveTimeBand,
  DRIVE_TIME_BANDS,
  driveTimeBand,
  IsochroneBand,
} from '../models/site-insights.model';
import { ThreePoint } from '../models/site-simulation.model';
import { TrafficStation } from '../models/traffic-station.model';
import { MapCoordinates } from './map-config.tokens';

/**
 * Derives a site-specific DTV three-point from the site's isochrone bands and the traffic-count
 * stations inside them. DTV here is *reachable* traffic: vehicles close enough that the site is
 * a plausible place to top up. That is what the two grouping rules encode — stations on
 * different roads add up, because each road is an independent approach to the site, while a road
 * counted at several points inside the catchment contributes once, because those stations
 * observe the same stream of vehicles.
 *
 * Pure and DI-free, like the simulation engine it feeds.
 */

/**
 * Share of a road's counted traffic credited to the site, by the band its counting station sits
 * in. Client-set weighting, stated only here: change these three values on request.
 */
export const DTV_BAND_WEIGHTS: Record<DriveTimeBand, number> = {
  '2min': 1,
  '5min': 0.6,
  '10min': 0.25,
};

/**
 * A catchment with no usable station is not a DTV of zero, so the two are separate cases rather
 * than a suggestion the caller has to recognise by its numbers.
 */
export type DtvSuggestion =
  | { readonly status: 'no-stations' }
  | { readonly status: 'suggested'; readonly dtv: ThreePoint };

/** The kept station of one road: its band's position in {@link DRIVE_TIME_BANDS}, and its DTV. */
interface RoadContribution {
  bandRank: number;
  dtv: number;
}

/**
 * Bands are keyed through the shared classifier rather than by raw `timeMinutes`: the stored
 * value is rounded server-side, so a raw-number lookup would not be total, and this keeps the
 * band a station is credited to identical to the band that colours it on the map. Two supplied
 * bands can therefore land on the same key.
 */
function polygonsByBand(
  bands: readonly IsochroneBand[],
): Map<DriveTimeBand, MapCoordinates[][]> {
  const byBand = new Map<DriveTimeBand, MapCoordinates[][]>();
  for (const band of bands) {
    const key = driveTimeBand(band.timeMinutes);
    const polygons = byBand.get(key);
    if (polygons) {
      polygons.push(band.polygon);
    } else {
      byBand.set(key, [band.polygon]);
    }
  }
  return byBand;
}

/**
 * Country-qualified so the 15 designations shared between DE and CH stay two roads; whitespace
 * removed so `A 3` and `A3` are one. The station's own country, not the site's — the site's is
 * constant across a catchment and would disambiguate nothing.
 */
function roadKey(station: TrafficStation): string | null {
  const designation = (station.road ?? '').replace(/\s+/gu, '').toUpperCase();
  return designation === '' ? null : `${station.country}:${designation}`;
}

/** Index of the smallest band containing `position`, or `-1` when no band does. */
function containingBandRank(
  position: MapCoordinates,
  polygonsPerBand: Map<DriveTimeBand, MapCoordinates[][]>,
): number {
  return DRIVE_TIME_BANDS.findIndex((band) =>
    (polygonsPerBand.get(band) ?? []).some((polygon) =>
      isPointInRing(position, polygon),
    ),
  );
}

export function deriveDtvSuggestion(
  bands: readonly IsochroneBand[],
  stations: readonly TrafficStation[],
): DtvSuggestion {
  const polygonsPerBand = polygonsByBand(bands);
  const roads = new Map<string, RoadContribution>();

  for (const station of stations) {
    const dtv = station.dtvKfzGesamt;
    const key = roadKey(station);
    if (dtv === null || key === null) {
      continue;
    }
    const bandRank = containingBandRank(
      { lat: station.latitude, lng: station.longitude },
      polygonsPerBand,
    );
    if (bandRank < 0) {
      continue;
    }
    const kept = roads.get(key);
    const wins =
      !kept ||
      bandRank < kept.bandRank ||
      (bandRank === kept.bandRank && dtv > kept.dtv);
    if (wins) {
      roads.set(key, { bandRank, dtv });
    }
  }

  if (roads.size === 0) {
    return { status: 'no-stations' };
  }

  let weighted = 0;
  for (const road of roads.values()) {
    weighted += road.dtv * DTV_BAND_WEIGHTS[DRIVE_TIME_BANDS[road.bandRank]];
  }

  // Fixed placeholder spread, not a data-derived uncertainty band. Deriving the bounds from the
  // already-rounded mode is what keeps `low <= mode <= high` true of the rounded values.
  const mode = Math.round(weighted);
  return {
    status: 'suggested',
    dtv: { mode, low: Math.round(mode * 0.5), high: mode * 2 },
  };
}
